import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { AdPosition, PositionConfig, UpdatePositionConfigRequest } from '../../types/Ad.api.type';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';

const POSITION_LABELS: Record<AdPosition, string> = {
  HOME_TOP: '홈 상단',
  HOME_MIDDLE: '홈 중단',
  MAIN_FEED_TOP: '메인 상단',
  MAIN_FEED: '메인 피드',
  MAIN_FEED_IMAGE: '메인 피드 이미지',
  POPULAR_FEED_TOP: '인기설문 상단',
  POPULAR_FEED: '인기설문',
  MY_FEED_TOP: '내설문 상단',
  MY_FEED: '내설문',
  DETAIL_COMMENT: '상세 댓글',
  ATTENDANCE: '출석 달력 하단',
  BENEFIT: '혜택 페이지',
  ATTENDANCE_RESULT: '출석 완료',
  EXCHANGE_RESULT: '환전 완료',
  WEEKLY_MISSION_RESULT: '미션 완료',
};

const ALL_POSITIONS: AdPosition[] = [
  'HOME_TOP',
  'HOME_MIDDLE',
  'MAIN_FEED_TOP',
  'MAIN_FEED',
  'POPULAR_FEED_TOP',
  'POPULAR_FEED',
  'MY_FEED_TOP',
  'MY_FEED',
  'DETAIL_COMMENT',
  'ATTENDANCE',
  'BENEFIT',
  'ATTENDANCE_RESULT',
  'EXCHANGE_RESULT',
  'WEEKLY_MISSION_RESULT',
];

interface LocalConfig {
  tossAdRatio: number;
  tossAdGroupId: string;
  isTossAdEnabled: boolean;
  tossImageAdGroupId: string;
  tossImageAdRatio: number;
  isTossImageAdEnabled: boolean;
  selfImageAdRatio: number;
  // S11-6 빈도 제한
  observationEnabled: boolean;
  frequencyLimitEnabled: boolean;
  dailyImpressionLimit: number | null;
}

const TossAdConfig = () => {
  const queryClient = useQueryClient();
  const [localConfigs, setLocalConfigs] = useState<Record<AdPosition, LocalConfig>>(
    {} as Record<AdPosition, LocalConfig>,
  );

  const { data: configs, isLoading } = useQuery({
    queryKey: ['positionConfigs'],
    queryFn: () => adApi.getPositionConfigs().then((res) => res.data),
  });

  useEffect(() => {
    if (!configs) return;

    const map: Record<string, LocalConfig> = {};
    for (const pos of ALL_POSITIONS) {
      const existing = configs.find((c: PositionConfig) => c.position === pos);
      map[pos] = {
        tossAdRatio: existing?.tossAdRatio ?? 30,
        tossAdGroupId: existing?.tossAdGroupId ?? '',
        isTossAdEnabled: existing?.isTossAdEnabled ?? false,
        tossImageAdGroupId: existing?.tossImageAdGroupId ?? '',
        tossImageAdRatio: existing?.tossImageAdRatio ?? 0,
        isTossImageAdEnabled: existing?.isTossImageAdEnabled ?? false,
        selfImageAdRatio: existing?.selfImageAdRatio ?? 0,
        observationEnabled: existing?.observationEnabled ?? false,
        frequencyLimitEnabled: existing?.frequencyLimitEnabled ?? false,
        dailyImpressionLimit: existing?.dailyImpressionLimit ?? null,
      };
    }
    setLocalConfigs(map as Record<AdPosition, LocalConfig>);
  }, [configs]);

  const updateMutation = useMutation({
    mutationFn: ({ position, data }: { position: AdPosition; data: UpdatePositionConfigRequest }) =>
      adApi.updatePositionConfig(position, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['positionConfigs'] });
      toast.success('설정이 저장되었습니다.');
    },
    onError: () => {
      toast.error('저장에 실패했습니다.');
    },
  });

  const handleSave = (position: AdPosition) => {
    const config = localConfigs[position];
    if (!config) return;

    // S11-6: 저장 시점 검증 (DB CHECK constraint 이중 방어)
    if (config.frequencyLimitEnabled && !config.observationEnabled) {
      toast.error('빈도 제한 활성화하려면 관측 대상이어야 합니다.');
      return;
    }

    const data: UpdatePositionConfigRequest = {
      tossAdRatio: config.tossAdRatio,
      tossAdGroupId: config.tossAdGroupId || null,
      isTossAdEnabled: config.isTossAdEnabled,
      // S11-6 빈도 제한
      observationEnabled: config.observationEnabled,
      frequencyLimitEnabled: config.frequencyLimitEnabled,
      dailyImpressionLimit: config.dailyImpressionLimit,
    };

    // 이미지배너 설정이 필요한 위치
    const imageAdPositions: AdPosition[] = [
      'MAIN_FEED',
      'ATTENDANCE_RESULT',
      'EXCHANGE_RESULT',
      'WEEKLY_MISSION_RESULT',
    ];
    if (imageAdPositions.includes(position)) {
      data.tossImageAdGroupId = config.tossImageAdGroupId || null;
      data.tossImageAdRatio = config.tossImageAdRatio;
      data.isTossImageAdEnabled = config.isTossImageAdEnabled;
      data.selfImageAdRatio = config.selfImageAdRatio;
    }

    updateMutation.mutate({ position, data });
  };

  const updateField = (position: AdPosition, field: keyof LocalConfig, value: string | number | boolean) => {
    setLocalConfigs((prev) => ({
      ...prev,
      [position]: { ...prev[position], [field]: value },
    }));
  };

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <p className="font-medium">토스 배너 광고 설정</p>
        <p className="mt-1">
          각 위치별로 토스 배너 광고와 자체 광고의 노출 비율을 설정합니다. 비율은 토스 광고가 노출될 확률(%)이며,
          나머지는 자체 광고가 노출됩니다.
        </p>
        <p className="mt-1">
          테스트 adGroupId: <code className="bg-blue-100 px-1 rounded">ait-ad-test-banner-id</code>
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {ALL_POSITIONS.map((position) => {
          const config = localConfigs[position];
          if (!config) return null;
          const showImageAdConfig = [
            'MAIN_FEED',
            'ATTENDANCE_RESULT',
            'EXCHANGE_RESULT',
            'WEEKLY_MISSION_RESULT',
          ].includes(position);

          return (
            <Card key={position}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{POSITION_LABELS[position]}</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <span className="text-xs text-gray-500">{config.isTossAdEnabled ? '활성' : '비활성'}</span>
                    <input
                      type="checkbox"
                      checked={config.isTossAdEnabled}
                      onChange={(e) => updateField(position, 'isTossAdEnabled', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* 토스/자체 비율 슬라이더 */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-gray-500">토스광고 비율</label>
                    <span className="text-xs font-medium">{config.tossAdRatio}%</span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={5}
                    value={config.tossAdRatio}
                    onChange={(e) => updateField(position, 'tossAdRatio', Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                    disabled={!config.isTossAdEnabled}
                  />
                  <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                    <span>자체 100%</span>
                    <span>토스 100%</span>
                  </div>
                </div>

                {/* adGroupId 입력 */}
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Ad Group ID</label>
                  <input
                    type="text"
                    value={config.tossAdGroupId}
                    onChange={(e) => updateField(position, 'tossAdGroupId', e.target.value)}
                    placeholder="토스 콘솔에서 발급받은 ID"
                    className="w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!config.isTossAdEnabled}
                  />
                </div>

                {/* MAIN_FEED 전용: 이미지배너 설정 */}
                {showImageAdConfig && (
                  <div className="border-t pt-3 mt-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-orange-600">이미지 배너</span>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <span className="text-xs text-gray-500">{config.isTossImageAdEnabled ? '활성' : '비활성'}</span>
                        <input
                          type="checkbox"
                          checked={config.isTossImageAdEnabled}
                          onChange={(e) => updateField(position, 'isTossImageAdEnabled', e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                      </label>
                    </div>

                    {/* 이미지배너 비율 (토스 당첨 내에서) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-gray-500">이미지배너 비율 (토스 내)</label>
                        <span className="text-xs font-medium">{config.tossImageAdRatio}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={config.tossImageAdRatio}
                        onChange={(e) => updateField(position, 'tossImageAdRatio', Number(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
                        disabled={!config.isTossImageAdEnabled}
                      />
                      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                        <span>기존배너 100%</span>
                        <span>이미지배너 100%</span>
                      </div>
                    </div>

                    {/* 이미지배너 adGroupId */}
                    <div>
                      <label className="text-xs text-gray-500 block mb-1">이미지배너 Ad Group ID</label>
                      <input
                        type="text"
                        value={config.tossImageAdGroupId}
                        onChange={(e) => updateField(position, 'tossImageAdGroupId', e.target.value)}
                        placeholder="이미지배너용 adGroupId"
                        className="w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                        disabled={!config.isTossImageAdEnabled}
                      />
                    </div>

                    {/* 내부 이미지광고 비율 (자체 당첨 내에서) */}
                    <div className="border-t pt-3 mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs text-gray-500">이미지광고 비율 (자체 내)</label>
                        <span className="text-xs font-medium">{config.selfImageAdRatio}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={config.selfImageAdRatio}
                        onChange={(e) => updateField(position, 'selfImageAdRatio', Number(e.target.value))}
                        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                      />
                      <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                        <span>기존광고 100%</span>
                        <span>이미지광고 100%</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* S11-6: 빈도 제한 */}
                <div className="border-t pt-3 mt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-purple-600">S11-6 빈도 제한</span>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.observationEnabled}
                      onChange={(e) => updateField(position, 'observationEnabled', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-xs text-gray-600">관측 대상 (baseline 데이터 축적)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config.frequencyLimitEnabled}
                      disabled={!config.observationEnabled}
                      onChange={(e) => updateField(position, 'frequencyLimitEnabled', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500 disabled:opacity-50"
                    />
                    <span className="text-xs text-gray-600">일일 상한 활성화 (tossAdRatio=0 오버라이드)</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">상한값</label>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      value={config.dailyImpressionLimit ?? ''}
                      disabled={!config.frequencyLimitEnabled}
                      onChange={(e) =>
                        updateField(
                          position,
                          'dailyImpressionLimit',
                          e.target.value === '' ? (null as unknown as number) : Number(e.target.value),
                        )
                      }
                      placeholder="무제한"
                      className="w-20 border rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:opacity-50"
                    />
                    <span className="text-xs text-gray-500">회/일</span>
                  </div>
                </div>

                {/* 저장 버튼 */}
                <Button
                  size="sm"
                  onClick={() => handleSave(position)}
                  disabled={updateMutation.isPending}
                  className="w-full flex items-center justify-center gap-1"
                >
                  <Save className="w-3.5 h-3.5" />
                  저장
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default TossAdConfig;
